import { ElementRef, RefObject, useCallback, useEffect } from 'react';

import imageCompression from 'browser-image-compression';
import { collection, doc, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import { ISanitizedCompany } from 'types/company';
import { IUserProfile, IUserResponse } from 'types/user';

import { normalizeTitle } from 'services/storySlug';
import { getShortUniqueId } from 'services/uniqueId';

import { db, storage } from 'clients/firebase';

import { useNotifications } from 'hooks/notifications';

import { Drawer } from 'components/shared/Drawer';

export const useDrawer = ({
  publishDrawerRef,
  user,
  company,
}: {
  publishDrawerRef: RefObject<ElementRef<typeof Drawer> | null>;
  user: IUserResponse;
  company: ISanitizedCompany;
}) => {
  const { addNotification } = useNotifications();

  // next/router's `reload()` is a full document reload, so calling
  // window.location.reload() directly is behaviour-identical and router-agnostic —
  // this hook is reached from UserMenu under both routers (#489).
  const reload = useCallback(() => window.location.reload(), []);

  const handleClose = useCallback(() => {
    publishDrawerRef.current?.closeDrawer();
  }, [publishDrawerRef]);

  const handleOpen = useCallback(() => {
    publishDrawerRef.current?.openDrawer();
  }, [publishDrawerRef]);

  useEffect(() => {
    if (!user.profile.firstName && company.name) {
      handleOpen();
    }
  }, [handleOpen, user.profile.firstName, company.name]);

  const handleSubmit = useCallback(
    async (values: IUserProfile) => {
      const { firstName = '', lastName = '', job = '', phone = '' } = values;
      let { avatar = '' } = values;

      try {
        if (typeof avatar === 'object') {
          avatar = await uploadProfilePic(avatar, values);
        }

        const collectionRef = collection(db, 'Users');
        const userRef = doc(collectionRef, user.uid);
        await setDoc(
          userRef,
          { profile: { firstName, lastName, job, phone, avatar } },
          { merge: true },
        );

        addNotification({ text: 'Profile saved' });
        reload();
      } catch (error) {
        addNotification({
          text: 'Error saving profile, Please try again later',
        });
      } finally {
        handleClose();
      }
    },
    [addNotification, handleClose, reload, user.uid],
  );

  return { handleOpen, handleClose, handleSubmit };
};

const uploadProfilePic = async (file: File, user: IUserProfile) => {
  const fileName = `${normalizeTitle(file.name)}-${getShortUniqueId()}`;
  const fileStorageLocation = `${user.companyId}/profile-pics/${fileName}`;
  const storageRef = ref(storage, fileStorageLocation);

  const compressedFile = await imageCompression(file, {
    maxSizeMB: 1,
    fileType: 'image/webp',
  });

  await uploadBytes(storageRef, compressedFile);

  return getDownloadURL(storageRef);
};
